/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useEffect, useState, useContext } from 'react';
import clsx from 'clsx';
import { Text, Types, Heading, Link, Icon } from '@marriott/mi-ui-library';
import { getUserDetails, useUserDetailsGlobalStore } from '@marriott/mi-store-utils';
import { useSession } from '@marriott/mi-headless-utils';
import { PageContext } from '../../modules';
import { isBonvoyCardMember } from '../../modules/utils';
import { NOTIFICATION_BANNER_KEYS, USER_STATE_AUTHENTICATED } from '../../modules/utils/constants/constants';
import { replaceText } from '../../modules/utils/promotionHelper';
import { getMemberLevelPropertiesForNotificationV2 } from './notificationHelper';
import {
  AccountOverviewNotificationModelV2,
  NotificationMessage,
  MemberProperties,
} from './AccountOverviewNotification.types';
import {
  StyledAccountOverviewNotification,
  StyledAccountOverviewNotificationV2,
} from './AccountOverviewNotification.styles';

export const AccountOverviewNotificationV2: FC<AccountOverviewNotificationModelV2> = pageProps => {
  const { datalayer } = useSession();
  const model = pageProps?.model;
  const pageContext = useContext(PageContext);
  const userState = pageContext?.userState;
  /** read data from global store */
  const { userProfileSummary } = useUserDetailsGlobalStore(state => state);
  const [memberProperties, setMemberProperties] = useState<MemberProperties>();
  const [isCobrandCardMember, setIsCobrandCardMember] = useState(false);
  const [updatedNotifcationMessages, setUpdatedNotifcationMessages] = useState(
    model?.fieldMessage?.map((item: NotificationMessage) => ({ ...item, showNotification: false }))
  );

  useEffect(() => {
    const notifcationMessages = updatedNotifcationMessages ? [...updatedNotifcationMessages] : [];

    if (notifcationMessages?.length > 0) {
      notifcationMessages?.forEach((item: NotificationMessage) => {
        if (item.key === NOTIFICATION_BANNER_KEYS.STAY_AND_PAY) {
          item.showNotification = true;
        }
      });

      setUpdatedNotifcationMessages(notifcationMessages);
    }
  }, []);

  useEffect(() => {
    if (datalayer && userState === USER_STATE_AUTHENTICATED) {
      setIsCobrandCardMember(isBonvoyCardMember(datalayer));
    }
  }, [datalayer]);

  useEffect(() => {
    if (userState === USER_STATE_AUTHENTICATED) {
      if (!userProfileSummary) {
        getUserDetails(true);
      } else {
        const notifcationMessages = updatedNotifcationMessages ? [...updatedNotifcationMessages] : [];
        const memberLevel = userProfileSummary?.level;
        let defaultBanner: NotificationMessage | undefined;
        let hasMemberLevelBanner = false;

        if (notifcationMessages?.length > 0) {
          notifcationMessages?.forEach((item: NotificationMessage) => {
            if (item.key === NOTIFICATION_BANNER_KEYS.COBRAND_LANE_MESSAGE_BANNER) {
              if (isCobrandCardMember) {
                setMemberProperties(
                  getMemberLevelPropertiesForNotificationV2(
                    userProfileSummary?.membershipCode,
                    pageContext?.memberStatusList,
                    userProfileSummary?.numberOfNights
                  )
                );
                item.showNotification = true;
              }
            }

            if (item.key === memberLevel) {
              hasMemberLevelBanner = true;
              setMemberProperties(
                getMemberLevelPropertiesForNotificationV2(
                  userProfileSummary?.membershipCode,
                  pageContext?.memberStatusList,
                  userProfileSummary?.numberOfNights
                )
              );
              item.showNotification = true;
            }
          });

          if (!hasMemberLevelBanner) {
            defaultBanner = notifcationMessages?.find(
              (item: NotificationMessage) => item.key === NOTIFICATION_BANNER_KEYS.MEMBER_ALL
            );
            if (defaultBanner) {
              setMemberProperties(
                getMemberLevelPropertiesForNotificationV2(
                  userProfileSummary?.membershipCode,
                  pageContext?.memberStatusList,
                  userProfileSummary?.numberOfNights
                )
              );
              defaultBanner.showNotification = true;
            }
          }

          const updatedMessages = notifcationMessages.map((item: NotificationMessage) =>
            item.key === defaultBanner?.key ? { ...item, ...defaultBanner } : item
          );

          setUpdatedNotifcationMessages(updatedMessages);
        }
      }
    }
  }, [userProfileSummary, isCobrandCardMember]);

  const showAccountNotification = updatedNotifcationMessages?.some(
    (msg: NotificationMessage) => msg.showNotification === true
  );

  return (
    <StyledAccountOverviewNotificationV2
      data-component-name="o-account-accountoverviewnotificationV2"
      className={clsx(model?.header || model?.subHeader || showAccountNotification ? 'pb-4' : '', model.styleClass)}
    >
      {(model?.header || model?.subHeader) && (
        <div className="container d-flex justify-content-between align-items-end flex-wrap header_wrapper">
          <div className="d-flex flex-column align-items-start">
            {model?.header && (
              <Heading
                variation={Types.headingType.title}
                fontSize={Types.size.small}
                element={Types.tags.h3}
                titleText={model?.header}
                customClass={'mb-0'}
              />
            )}
            {model?.subHeader && (
              <Text
                customClass="t-font-m mt-2"
                copyText={model?.subHeader}
                fontSize={Types.size.medium}
                element={Types.tags.paragraph}
              />
            )}
          </div>
          <div>
            {model?.earningIcon && <Icon iconClass={`${model.earningIcon} video-icon px-1`} />}
            {model?.brandEarningsLink && (
              <Link
                linkClassName="m-link-tertiary-button mb-2"
                target="_self"
                text={model?.brandEarningsLabel}
                linkHref={model?.brandEarningsLink || ''}
                custom_click_track_value={model?.brandClickTrackValue}
              />
            )}
          </div>
        </div>
      )}
      {showAccountNotification &&
        updatedNotifcationMessages?.map((msg: NotificationMessage) => {
          const {
            warningMessage,
            icon,
            theme,
            showNotification,
            isExternalLink,
            ctaLabel,
            ctaPath,
            key,
            clickTrackValue,
          } = msg;
          return showNotification ? (
            <StyledAccountOverviewNotification
              className={clsx('container', model?.header ? 'pt-4' : '')}
              iconTheme={theme}
              bannerBackground={memberProperties?.backgroundColor}
              bannerBorder={memberProperties?.borderLeft}
              data-testid="Notification"
            >
              <div className="col-sm-12 p-sm-0">
                <div className="card d-flex align-items-start align-items-md-center py-3 flex-sm-row flex-lg-row mb-4 mb-md-0 notification-banner">
                  <div className="d-flex outer ml-3 ml-md-4">
                    <div className="icon d-flex icon-size">
                      <span className={`icon-l icon-inverse mx-auto my-auto ${icon}`}></span>
                    </div>
                  </div>
                  <div className="d-flex navheader">
                    <div className="ml-3 ml-lg-4 mr-3 mr-md-4 mr-lg-0">
                      <Text
                        element={Types.tags.span}
                        fontSize={Types.size.medium}
                        copyText={replaceText(warningMessage || '', [
                          memberProperties?.bonusPoints || '',
                          memberProperties?.memberLevel || '',
                        ])}
                      />
                    </div>
                    {msg?.ctaPath && (
                      <div className="ml-3 ml-md-0 mr-sm-5 mr-md-5 ml-md-3 mt-3 mt-lg-0 mb-2 text-nowrap">
                        <Link
                          linkClassName={clsx(
                            isExternalLink ? 'm-link-tertiary-button-external' : 'm-link-tertiary-button'
                          )}
                          target={isExternalLink ? '_blank' : '_self'}
                          text={ctaLabel}
                          linkHref={ctaPath}
                          custom_click_track_value={clickTrackValue}
                          data-testid={`${key}Cta`}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </StyledAccountOverviewNotification>
          ) : (
            ''
          );
        })}
    </StyledAccountOverviewNotificationV2>
  );
};
